import { useForm } from "@tanstack/react-form";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { FormField } from "#/components/checkout/FormField";
import { OrderShipments } from "#/components/order-status/OrderShipments";
import { StatusTracker } from "#/components/order-status/StatusTracker";
import { Input } from "#/components/ui/input";
import { ASSETS } from "#/lib/assets";
import { formatMoney } from "#/lib/medusa-types/commerce";
import { useAftershipTracking } from "#/queries/aftership";
import { useOrderByDisplayId } from "#/queries/orders";
import type {
  AftershipCheckpoint,
  AftershipTag,
  AftershipTracking,
} from "#/services/aftership-service";
import { PillHero } from "../components/PillHero.js";
import { PillBtn } from "../components/primitives.js";

const trackSearchSchema = z.object({
  email: z.string().optional().catch(undefined),
  code: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/track")({
  validateSearch: trackSearchSchema,
  head: () => ({ meta: [{ title: "Track Order" }] }),
  component: TrackPage,
});

const trackSchema = z.object({
  email: z.email("Enter a valid email"),
  code: z.string().min(1, "Enter your order number"),
});

type TrackForm = z.infer<typeof trackSchema>;

const EMPTY: TrackForm = { email: "", code: "" };

function TrackPage() {
  // A native (pre-hydration / no-JS) form submit reaches the page as
  // ?email&code; seed the lookup from it so tracking resolves without waiting
  // for the client to hydrate.
  const search = Route.useSearch();
  const seeded =
    search.email && search.code
      ? { email: search.email.trim(), code: search.code.trim() }
      : null;
  const [submitted, setSubmitted] = useState<TrackForm | null>(seeded);
  const { data: order, isPending } = useOrderByDisplayId(
    submitted?.code,
    submitted?.email,
  );

  const form = useForm({
    defaultValues: seeded ?? EMPTY,
    validators: { onChange: trackSchema, onSubmit: trackSchema },
    onSubmit: ({ value }) => {
      setSubmitted({ code: value.code.trim(), email: value.email.trim() });
    },
  });

  const reset = () => {
    setSubmitted(null);
    form.reset();
  };

  return (
    <div data-screen-label="Track" className="pb-24">
      <section className="pt-12 md:pt-20">
        <div className="mx-auto w-full max-w-figma px-6 md:px-10">
          <h1 className="m-0 font-bold font-display text-4xl md:text-6xl text-lp-green-dk leading-none tracking-tight">
            TRACK YOUR ORDER
          </h1>
          <p className="mt-4 font-display font-semibold text-lg md:text-2xl text-lp-green-mid">
            Your books are on their way.
          </p>
        </div>
      </section>
      <PillHero
        pillSide="left"
        content={
          <form
            className="w-full"
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
          >
            <div className="mt-6 flex flex-col gap-4">
              <form.Field name="email">
                {(field) => (
                  <FormField field={field} label="Email" type="email" />
                )}
              </form.Field>
              <form.Field name="code">
                {(field) => <FormField field={field} label="Order number" />}
              </form.Field>
              <PillBtn
                type="submit"
                variant="green"
                size="md"
                className="mt-2 w-full"
              >
                Track
              </PillBtn>
            </div>
          </form>
        }
        asset={
          <img
            src={ASSETS.heroTrack}
            alt=""
            className="h-72 w-auto object-cover md:h-124"
          />
        }
        assetAlign="edge"
        assetClassName="md:-my-28 md:-mr-24"
      />

      {submitted != null && (
        <section className="mx-auto mt-16 w-full max-w-3xl px-6 md:px-10">
          {isPending ? (
            <div className="py-12 text-center font-display text-lp-text-lt">
              Looking up #{submitted.code}…
            </div>
          ) : !order ? (
            <div className="rounded-3xl bg-lp-green-soft p-10 text-center font-display text-lp-text">
              <div className="mb-2 font-bold text-lp-green-dk text-xl">
                No order found
              </div>
              <p className="m-0 mb-6">
                We couldn't find an order with number{" "}
                <strong className="text-lp-text-dk">{submitted.code}</strong>.
                Double-check the number and email, then try again.
              </p>
              <button
                type="button"
                onClick={reset}
                className="cursor-pointer border-0 bg-transparent font-display font-semibold text-lp-green-dk underline underline-offset-4 hover:text-lp-green-mid"
              >
                Try a different number
              </button>
            </div>
          ) : (
            <div className="rounded-3xl bg-lp-green-soft p-10">
              <div className="mb-2 font-bold font-display text-2xl text-lp-green-dk">
                Order #{order.display_id ?? order.id.slice(0, 8)}
              </div>
              <div className="mb-1 font-display text-lp-text capitalize">
                Status: <strong>{order.status}</strong>
              </div>
              <div className="mb-8 font-display text-lp-text-lt text-sm">
                Total:{" "}
                <span className="font-mono text-lp-text">
                  {formatMoney(Number(order.total ?? 0), order.currency_code)}
                </span>
              </div>
              <StatusTracker
                status={order.status}
                payment_status={order.payment_status}
                fulfillment_status={order.fulfillment_status}
              />
              <div className="mt-6">
                <OrderShipments order={order} />
              </div>
            </div>
          )}
        </section>
      )}

      <AftershipEmbed />
    </div>
  );
}

function AftershipEmbed() {
  const [draft, setDraft] = useState("");
  const [trackingNumber, setTrackingNumber] = useState<string | null>(null);
  const { data, isPending, isError, error } =
    useAftershipTracking(trackingNumber);

  return (
    <section className="mx-auto mt-16 w-full max-w-3xl px-6 md:px-10">
      <div className="rounded-3xl bg-white p-8 ring-1 ring-lp-green-mid/20">
        <h2 className="m-0 mb-2 font-bold font-display text-2xl text-lp-green-dk">
          Have a tracking number?
        </h2>
        <p className="m-0 mb-6 font-display text-lp-text">
          Enter the courier tracking number from your shipping confirmation
          email and we'll show its live status from Aftership.
        </p>
        <form
          className="flex flex-col gap-3 sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            const next = draft.trim();
            setTrackingNumber(next.length > 0 ? next : null);
          }}
        >
          <Input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. LBC123456789PH"
            autoComplete="off"
            className="flex-1"
          />
          <PillBtn type="submit" variant="green" size="sm">
            Track
          </PillBtn>
        </form>

        {trackingNumber && (
          <div className="mt-8">
            {isPending ? (
              <div className="py-10 text-center font-display text-lp-text-lt">
                Looking up {trackingNumber}…
              </div>
            ) : isError ? (
              <AftershipMessage
                title="We couldn't reach the carrier"
                body={
                  error instanceof Error
                    ? error.message
                    : "Please try again in a moment."
                }
              />
            ) : !data ? (
              <AftershipMessage
                title="No tracking found"
                body={`We couldn't match "${trackingNumber}" to a courier. Double-check the number and try again.`}
              />
            ) : (
              <AftershipTimeline tracking={data} />
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function AftershipMessage({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-2xl bg-lp-green-soft p-6 text-center font-display">
      <div className="font-bold text-lp-green-dk text-lg">{title}</div>
      <p className="m-0 mt-2 text-lp-text">{body}</p>
    </div>
  );
}

const TAG_LABEL: Record<AftershipTag, string> = {
  Pending: "Pending",
  InfoReceived: "Info received",
  InTransit: "In transit",
  OutForDelivery: "Out for delivery",
  AttemptFail: "Delivery attempted",
  Delivered: "Delivered",
  AvailableForPickup: "Available for pickup",
  Exception: "Exception",
  Expired: "Expired",
};

const TAG_TONE: Record<AftershipTag, string> = {
  Pending: "bg-lp-grey/30 text-lp-text-dk",
  InfoReceived: "bg-lp-grey/30 text-lp-text-dk",
  InTransit: "bg-lp-green-soft text-lp-green-dk",
  OutForDelivery: "bg-lp-green-soft text-lp-green-dk",
  AttemptFail: "bg-amber-100 text-amber-900",
  Delivered: "bg-lp-green-mid text-white",
  AvailableForPickup: "bg-lp-green-soft text-lp-green-dk",
  Exception: "bg-red-100 text-red-900",
  Expired: "bg-lp-grey/40 text-lp-text-dk",
};

function AftershipTimeline({ tracking }: { tracking: AftershipTracking }) {
  const checkpoints = [...tracking.checkpoints].sort(
    (a, b) =>
      new Date(b.checkpoint_time).getTime() -
      new Date(a.checkpoint_time).getTime(),
  );

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-3 border-lp-grey/40 border-b pb-4">
        <div>
          <div className="font-display text-lp-text-lt text-sm uppercase tracking-wide">
            {tracking.slug}
          </div>
          <div className="font-bold font-display text-lp-text-dk text-xl">
            {tracking.tracking_number}
          </div>
        </div>
        <span
          className={`rounded-full px-3 py-1 font-display font-semibold text-sm ${TAG_TONE[tracking.tag]}`}
        >
          {TAG_LABEL[tracking.tag]}
        </span>
      </div>

      {tracking.expected_delivery && (
        <div className="mt-4 font-display text-lp-text">
          Expected delivery:{" "}
          <strong className="text-lp-text-dk">
            {formatCheckpointDate(tracking.expected_delivery)}
          </strong>
        </div>
      )}

      {checkpoints.length === 0 ? (
        <p className="mt-6 font-display text-lp-text-lt">
          The carrier hasn't reported any updates yet. Check back soon.
        </p>
      ) : (
        <ol className="mt-6 space-y-5 border-lp-green-mid/30 border-l-2 pl-6">
          {checkpoints.map((cp, i) => (
            <AftershipCheckpointItem
              key={`${cp.checkpoint_time}-${i}`}
              checkpoint={cp}
              isLatest={i === 0}
            />
          ))}
        </ol>
      )}

      {tracking.courier_tracking_link && (
        <a
          href={tracking.courier_tracking_link}
          target="_blank"
          rel="noreferrer noopener"
          className="mt-6 inline-block font-display font-semibold text-lp-green-dk underline underline-offset-4 hover:text-lp-green-mid"
        >
          View on courier site →
        </a>
      )}
    </div>
  );
}

function AftershipCheckpointItem({
  checkpoint,
  isLatest,
}: {
  checkpoint: AftershipCheckpoint;
  isLatest: boolean;
}) {
  const location = [checkpoint.city, checkpoint.state, checkpoint.country_name]
    .filter(Boolean)
    .join(", ");

  return (
    <li className="relative">
      <span
        className={`-left-[31px] absolute top-1.5 h-3 w-3 rounded-full ring-2 ring-white ${
          isLatest ? "bg-lp-green-dk" : "bg-lp-green-mid/60"
        }`}
      />
      <div className="font-display text-lp-text-lt text-sm">
        {formatCheckpointDate(checkpoint.checkpoint_time)}
        {location && <span> · {location}</span>}
      </div>
      <div className="mt-1 font-display text-lp-text-dk">
        {checkpoint.message || checkpoint.subtag_message}
      </div>
    </li>
  );
}

function formatCheckpointDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
